import { __assign, __rest } from 'tslib';
import { useLocalization } from '@shared-ui/localization-context';
import { useRef, createElement, useState, Children, useCallback, useEffect, Fragment, cloneElement, useMemo } from 'react';
import { debounce } from 'throttle-debounce';
import { classNames, liveAnnounce, Level, analyticsTracker } from 'uitk-react-utils';
import { UitkPagingPrevButton, UitkPagingNextButton } from 'uitk-react-button';

var UitkCarouselNav = function (props) {
    var analytics = props.analytics, buttonText = props.buttonText, carouselName = props.carouselName, currentIndex = props.currentIndex, itemCount = props.itemCount, itemsVisible = props.itemsVisible, onNextCb = props.onNextCb, onPrevCb = props.onPrevCb, navigationIconId = props.navigationIconId, size = props.size;
    var currentItemsVisible = itemsVisible[size];
    var prevBtnClassNames = classNames('uitk-carousel-button-paging', 'uitk-carousel-button-prev', typeof window !== 'undefined' && 'ontouchstart' in window && 'is-visually-hidden');
    var nextBtnClassNames = classNames('uitk-carousel-button-paging', 'uitk-carousel-button-next', typeof window !== 'undefined' && 'ontouchstart' in window && 'is-visually-hidden');
    var nextBtnRef = useRef(null);
    var prevBtnRef = useRef(null);
    var clickLeftAnalytics = analytics && __assign({ localAnalyticsId: carouselName + ".Carousel." + UitkCarouselLocalAnalyticsIdType.clickLeft }, analytics);
    var clickRightAnalytics = analytics && __assign({ localAnalyticsId: carouselName + ".Carousel." + UitkCarouselLocalAnalyticsIdType.clickRight }, analytics);
    var focusNavigationBtn = function (ref) {
        if (ref && ref.current) {
            ref.current.focus();
        }
    };
    var onPrev = function () {
        if (currentItemsVisible > 0 && currentIndex === 1) {
            focusNavigationBtn(prevBtnRef);
        }
        onPrevCb && onPrevCb();
    };
    var onNext = function () {
        if (currentItemsVisible > 0 && currentIndex === itemCount - currentItemsVisible - 1) {
            focusNavigationBtn(nextBtnRef);
        }
        onNextCb && onNextCb();
    };
    return typeof window !== 'undefined' && itemCount > currentItemsVisible ? (createElement("div", { className: "uitk-carousel-controls" },
        createElement(UitkPagingPrevButton, { analytics: clickLeftAnalytics, className: prevBtnClassNames, iconId: navigationIconId === null || navigationIconId === void 0 ? void 0 : navigationIconId.prev, iconTitle: buttonText.prevButton, onClick: onPrev, domRef: prevBtnRef, "data-stid": "carousel-nav-prev", "aria-hidden": currentIndex === 0, disabled: currentIndex === 0 }),
        createElement(UitkPagingNextButton, { analytics: clickRightAnalytics, className: nextBtnClassNames, iconId: navigationIconId === null || navigationIconId === void 0 ? void 0 : navigationIconId.next, iconTitle: buttonText.nextButton, onClick: onNext, domRef: nextBtnRef, "data-stid": "carousel-nav-next", "aria-hidden": currentIndex === itemCount - currentItemsVisible, disabled: currentIndex === itemCount - currentItemsVisible }))) : null;
};
UitkCarouselNav.displayName = 'UitkCarouselNav';

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function useReferredState(initialValue) {
    var _a = useState(initialValue), state = _a[0], setState = _a[1];
    var reference = useRef(state);
    var setReferredState = function (value) {
        reference.current = value;
        setState(value);
    };
    return [reference, setReferredState];
}
var detectIE = function () {
    var _a;
    // https://stackoverflow.com/questions/19999388/check-if-user-is-using-ie
    var ua = (_a = window === null || window === void 0 ? void 0 : window.navigator) === null || _a === void 0 ? void 0 : _a.userAgent;
    return ua.indexOf('Trident/') > 0 || ua.indexOf('Edge/') > 0;
};

var UitkCarouselLocalAnalyticsIdType;
(function (UitkCarouselLocalAnalyticsIdType) {
    UitkCarouselLocalAnalyticsIdType["clickLeft"] = "clickLeft";
    UitkCarouselLocalAnalyticsIdType["clickRight"] = "clickRight";
    UitkCarouselLocalAnalyticsIdType["scrollLeft"] = "scrollLeft";
    UitkCarouselLocalAnalyticsIdType["scrollRight"] = "scrollRight";
})(UitkCarouselLocalAnalyticsIdType || (UitkCarouselLocalAnalyticsIdType = {}));
var Carousel = function (props) {
    var analytics = props.analytics, buttonText = props.buttonText, carouselName = props.carouselName, className = props.className, children = props.children, itemsVisible = props.itemsVisible, navigationIconId = props.navigationIconId, pageBy = props.pageBy, peekWidth = props.peekWidth, wrapItems = props.wrapItems;
    var _a = useState(false), mounted = _a[0], setMounted = _a[1];
    var _b = useReferredState(0), currentIndexRef = _b[0], setCurrentIndex = _b[1];
    var currentIndex = currentIndexRef.current;
    var _c = useState(false), isTouch = _c[0], setIsTouch = _c[1];
    var _d = useReferredState(''), sizeRef = _d[0], setSize = _d[1];
    var size = sizeRef.current;
    var NAV_WITH_BTN = useRef(false);
    var AVOID_SCROLL_HANDLER = useRef(false);
    // Viewport for IE-11 fallback
    var MD_MIN = 576;
    var LG_MIN = 992;
    var XL_MIN = 1200;
    // Interaction Timer of resize handler
    var INTERACTION_TIMER = 200;
    var ITEM_PADDING = 12;
    var PEEK_WIDTH = useRef(12);
    var itemCount = Children.count(children);
    var selfRef = useRef(null);
    var containerRef = useRef(null);
    // Below Values are reffed because we want them to be maintained between renders
    var viewed = useRef([]);
    var scrollDistance = useRef(0);
    // A11y related functions -- Starts
    var announceCards = function () {
        // @ts-ignore
        var visibleCardHeadingsElements = containerRef.current.querySelectorAll("\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h1:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h2:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h3:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h4:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h5:not([aria-hidden=\"true\"]),\n          .uitk-carousel-item:not([aria-hidden=\"true\"]) h6:not([aria-hidden=\"true\"])\n        ");
        var visibleCardHeadings = Array.from(visibleCardHeadingsElements).map(function (heading) { return heading.innerText || heading.textContent; });
        setTimeout(function () {
            liveAnnounce(props.l10n.formatText('carousel.cardChange', visibleCardHeadings.join(',')), Level.POLITE);
        }, 200);
    };
    var setTabIndex = function () {
        // @ts-ignore
        var carouselItem = containerRef.current.querySelectorAll('.uitk-carousel-item');
        var carouselItemArray = Array.from(carouselItem);
        carouselItemArray.forEach(function (item) {
            var carouselElement = item.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            var carouselElementArray = Array.from(carouselElement);
            carouselElementArray.forEach(function (elem) {
                return elem[item.hasAttribute('aria-hidden') ? 'setAttribute' : 'removeAttribute']('tabindex', '-1');
            });
        });
    };
    // A11y related functions -- Ends
    // Analytics functions -- Starts
    var viewItemAnalytics = function () {
        var vis = itemsVisible[sizeRef.current];
        if (peekWidth) {
            vis += 1;
        }
        for (var i = 0; i < vis && currentIndex + i < Children.count(children); ++i) {
            if (!viewed.current[currentIndex + i]) {
                viewed.current[currentIndex + i] = true;
                if (typeof props.itemViewedCallback === 'function') {
                    props.itemViewedCallback(currentIndex + i);
                }
                if (analytics) {
                    analyticsTracker(analytics, carouselName + ".Carousel.itemViewed." + (currentIndex + i), '');
                }
            }
        }
    };
    var scrollAnalytics = function (newIndex, currentIndexItem) {
        if (analytics) {
            analyticsTracker(analytics, carouselName + ".Carousel." + (newIndex > currentIndexItem
                ? UitkCarouselLocalAnalyticsIdType.scrollRight
                : UitkCarouselLocalAnalyticsIdType.scrollLeft), '');
        }
    };
    // Analytics functions -- Ends
    // Carousel main functions -- Starts
    var calcUpdatedCurrentIndex = function () {
        // @ts-ignore
        var _a = containerRef.current, scrollLeft = _a.scrollLeft, scrollWidth = _a.scrollWidth;
        // @ts-ignore
        var containerClienWidth = containerRef.current.getBoundingClientRect().width;
        var effectiveScrollDistance = scrollDistance.current;
        var absoluteScrollLeft = Math.abs(scrollLeft);
        // Edge case for last item of carousel in view along with peaking last element of previous frame.
        // when scroll reaches the end of container then scrollLeft should be equal to scrollWidth - containerClienWidth
        // but as subpixels are rounded to nearest interger this calc fails so added -5 to overcome it
        // @ts-ignore
        var edgeCase = absoluteScrollLeft > Math.round(scrollWidth - containerClienWidth - 5) ? 1 : 0;
        var newIndex = Math.floor(absoluteScrollLeft / effectiveScrollDistance) + edgeCase;
        return newIndex;
    };
    var scrollHandler = useCallback(debounce(INTERACTION_TIMER, function () {
        if (AVOID_SCROLL_HANDLER.current) {
            AVOID_SCROLL_HANDLER.current = false;
            return false;
        }
        var newIndex = calcUpdatedCurrentIndex();
        if (newIndex !== currentIndexRef.current) {
            scrollAnalytics(newIndex, currentIndexRef.current);
            setCurrentIndex(newIndex);
            viewItemAnalytics();
        }
    }), []);
    // Scrolls the carousel to leftDistance pixels from the left edge.
    var scrollCarousel = function (leftDistance) {
        var _a;
        // @ts-ignore
        containerRef.current.scrollTo({
            behavior: 'smooth',
            left: leftDistance * (((_a = document === null || document === void 0 ? void 0 : document.querySelector('html')) === null || _a === void 0 ? void 0 : _a.getAttribute('dir')) === 'rtl' ? -1 : 1)
        });
    };
    var getCarouselBreakPoints = function () {
        // Get breakpoint from css variables for IE-11 get from hardcoded values
        // Rem to px
        MD_MIN =
            // @ts-ignore
            parseInt(getComputedStyle(selfRef === null || selfRef === void 0 ? void 0 : selfRef.current).getPropertyValue('--carousel-breakpoint-md'), 10) * 16 || MD_MIN;
        LG_MIN =
            // @ts-ignore
            parseInt(getComputedStyle(selfRef === null || selfRef === void 0 ? void 0 : selfRef.current).getPropertyValue('--carousel-breakpoint-lg'), 10) * 16 || LG_MIN;
        XL_MIN =
            // @ts-ignore
            parseInt(getComputedStyle(selfRef === null || selfRef === void 0 ? void 0 : selfRef.current).getPropertyValue('--carousel-breakpoint-xl'), 10) * 16 || XL_MIN;
    };
    var setScrollDistance = function () {
        var viewportSize = sizeRef.current;
        if (!viewportSize) {
            return false;
        }
        // For backward compatibilty & IE fallback to 12px peek
        var isIE = detectIE();
        if (typeof peekWidth === 'number' && !isIE && PEEK_WIDTH.current !== peekWidth) {
            PEEK_WIDTH.current = peekWidth;
            // @ts-ignore
            selfRef.current.style.setProperty('--peek-width', PEEK_WIDTH.current + "px");
        }
        else if (typeof peekWidth === 'object' && !isIE && PEEK_WIDTH.current !== peekWidth[viewportSize]) {
            // Backward compatibility if xl is not passed
            viewportSize = viewportSize === 'xl' && !peekWidth.xl ? 'lg' : viewportSize;
            // @ts-ignore
            PEEK_WIDTH.current = peekWidth[viewportSize];
            // @ts-ignore
            selfRef.current.style.setProperty('--peek-width', PEEK_WIDTH.current + "px");
        }
        var effectivePeekWidth = peekWidth ? ITEM_PADDING + PEEK_WIDTH.current : 0;
        var itemsInView = itemsVisible[viewportSize];
        // @ts-ignore
        var containerClienWidth = containerRef.current.getBoundingClientRect().width;
        // @ts-ignore
        scrollDistance.current =
            containerRef.current &&
                // @ts-ignore
                (containerClienWidth - (itemsInView - 1) * ITEM_PADDING - effectivePeekWidth) / itemsInView + ITEM_PADDING;
    };
    var setViewportSize = function () {
        // @ts-ignore
        var width = selfRef.current.getBoundingClientRect().width;
        setScrollDistance();
        // Responsible for control of carousel buttons
        if (width < MD_MIN && size !== 'sm') {
            setSize('sm');
        }
        else if (width < LG_MIN && width >= MD_MIN && size !== 'md') {
            setSize('md');
        }
        else if (width < XL_MIN && width >= LG_MIN && size !== 'lg') {
            setSize('lg');
        }
        else if (width >= XL_MIN) {
            // Backward compatibility if xl is not passed
            if (itemsVisible.xl) {
                setSize('xl');
            }
            else {
                setSize('lg');
            }
        }
    };
    var page = function (direction) {
        var capPageBy = pageBy > itemsVisible[size] ? itemsVisible[size] : pageBy;
        var index = direction === 1 ? currentIndex + Number(capPageBy) : currentIndex - Number(capPageBy);
        var limit = itemCount - itemsVisible[size];
        index = index < limit ? index : limit;
        if (index < 0) {
            index = 0;
        }
        NAV_WITH_BTN.current = true;
        setCurrentIndex(index);
    };
    var onPrevCb = function () {
        page(-1);
    };
    var onNextCb = function () {
        page(1);
    };
    var renderCarousel = function () {
        var _a;
        var setItemsCSSVariable = typeof window !== 'undefined' && !detectIE() && itemsVisible[sizeRef.current];
        var styleObj = {};
        if (setItemsCSSVariable) {
            styleObj['--carousel-item-count'] = itemsVisible[sizeRef.current];
        }
        var cssClassNames = classNames('uitk-carousel', !peekWidth && 'no-peek', !isTouch && 'no-touch', !mounted && 'no-js', className, (_a = {}, _a[sizeRef.current] = !!sizeRef.current && typeof window !== 'undefined' && detectIE(), _a));
        var navProps = {
            analytics: analytics,
            buttonText: buttonText,
            carouselName: carouselName,
            currentIndex: currentIndex,
            itemCount: itemCount,
            itemsVisible: itemsVisible,
            navigationIconId: navigationIconId,
            onNextCb: onNextCb,
            onPrevCb: onPrevCb,
            pageBy: pageBy,
            peekWidth: peekWidth,
            size: size
        };
        return (createElement(Fragment, null,
            createElement("div", { className: cssClassNames, ref: selfRef, "data-stid": "carousel-wrapper" },
                createElement("div", { className: "uitk-carousel-container", ref: containerRef, onScroll: scrollHandler, "data-stid": "carousel-container", style: styleObj },
                    createElement(UitkCarouselItemList, { itemsVisible: itemsVisible, size: size, currentIndex: currentIndex, peekWidth: peekWidth, wrapItems: wrapItems }, children)),
                createElement(UitkCarouselNav, __assign({}, navProps)))));
    };
    // Hooks
    useEffect(function () {
        mounted && announceCards();
        // UseEffect for carousel nav btns
        // return if this side effect triggered by scrollhandler
        if (NAV_WITH_BTN.current) {
            NAV_WITH_BTN.current = false;
            AVOID_SCROLL_HANDLER.current = true;
            scrollCarousel(currentIndex * scrollDistance.current);
            viewItemAnalytics();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex]);
    useEffect(function () {
        var debounceResizeHandler = debounce(INTERACTION_TIMER, function () {
            setViewportSize();
            var updateCurrentIndexOnResize = calcUpdatedCurrentIndex();
            if (updateCurrentIndexOnResize !== currentIndexRef.current) {
                setCurrentIndex(updateCurrentIndexOnResize);
            }
        });
        setMounted(true);
        if ('ontouchstart' in window) {
            setIsTouch(true);
        }
        getCarouselBreakPoints();
        window.addEventListener('resize', debounceResizeHandler);
        setViewportSize();
        // Adds empty aria-live region to page to ensure Jaws screen reader
        // announces on initial key press. Relates to https://jira.expedia.biz/browse/WCAG-8258
        liveAnnounce('');
        viewItemAnalytics();
        return function () { return window.removeEventListener('resize', debounceResizeHandler); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(function () {
        setScrollDistance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [size]);
    useEffect(function () {
        var onChangeCB = props.onChangeCB;
        if (onChangeCB) {
            onChangeCB(containerRef);
        }
        setTabIndex();
    });
    // Carousel main functions -- Ends
    return renderCarousel();
};
var UitkCarousel = function (props) {
    var l10n = useLocalization();
    return createElement(Carousel, __assign({ l10n: l10n }, props));
};
UitkCarousel.displayName = 'UitkCarousel';
UitkCarousel.defaultProps = {
    navigationIconId: {
        next: 'next-button',
        prev: 'prev-button'
    },
    wrapItems: true
};

var UitkCarouselItem = function (props) {
    var children = props.children, wrapItems = props.wrapItems, ariaHidden = props.ariaHidden;
    var baseClassName = "uitk-carousel-item";
    var toggleAriaHidden = function (item) {
        var itemClassName = wrapItems ? item.props.className : '';
        var itemProps = __assign(__assign({}, (ariaHidden && { 'aria-hidden': true, tabIndex: -1 })), { className: itemClassName ? itemClassName + " " + baseClassName : baseClassName, 'data-stid': 'carousel-item' });
        if (!wrapItems) {
            return cloneElement(item, itemProps);
        }
        return createElement("li", __assign({}, itemProps), item);
    };
    var renderCarouselItem = function () {
        var item = children;
        return toggleAriaHidden(item);
    };
    return renderCarouselItem();
};
UitkCarouselItem.displayName = 'UitkCarouselItem';
UitkCarouselItem.defaultProps = {
    wrapItems: true
};

var UitkCarouselBreakPointSizeMap = [
    ['sm', 'sm'],
    ['md', 'md'],
    ['lg', 'lg'],
    ['xl', 'xl']
];
var UitkCarouselBreakPointSizes = new Map(UitkCarouselBreakPointSizeMap);

var UitkCarouselItemList = function (props) {
    var children = props.children, size = props.size, currentIndex = props.currentIndex, itemProps = __rest(props, ["children", "size", "currentIndex"]);
    var baseClassName = 'uitk-carousel-item-container';
    var _a = useState(false), mounted = _a[0], setMounted = _a[1];
    var getCarouselItemClass = function () {
        var carouselItemContainerClass = baseClassName;
        if (typeof window !== 'undefined' && mounted) {
            var isIE = detectIE();
            if (isIE) {
                var carouselItemBaseClassName_1 = 'uitk-carousel-item';
                var peek_1 = itemProps.peekWidth ? '-peek' : '';
                var carouselItemViewportClasses_1 = [];
                UitkCarouselBreakPointSizes.forEach(function (value) {
                    // Backward compatibility if xl is not passed
                    if (value === 'xl' && !itemProps.itemsVisible[value]) {
                        return;
                    }
                    carouselItemViewportClasses_1.push(carouselItemBaseClassName_1 + "-" + value + "-" + itemProps.itemsVisible[value] + peek_1);
                });
                return classNames(carouselItemContainerClass, carouselItemViewportClasses_1);
            }
            var mountedClass = itemProps.peekWidth ? 'mounted-peek' : 'mounted';
            return classNames(baseClassName, mountedClass);
        }
        return classNames(baseClassName, { 'ssr-peek': itemProps.peekWidth });
    };
    var carouselItemListClass = useMemo(function () { return getCarouselItemClass(); }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemProps.peekWidth, itemProps.itemsVisible, mounted]);
    var getCarouselItems = function () {
        var items = children;
        var currentItemsVisible = props.itemsVisible[size];
        var startOfCurrentGroup = currentIndex;
        var startOfNextGroup = currentIndex + currentItemsVisible;
        var elements = items.map(function (elm, i) {
            var ariaHidden = typeof currentItemsVisible === 'undefined' ? false : i < startOfCurrentGroup || i >= startOfNextGroup;
            return (createElement(UitkCarouselItem, __assign({ key: i, ariaHidden: ariaHidden }, itemProps), elm));
        });
        return elements;
    };
    useEffect(function () {
        setMounted(true);
    }, []);
    return (createElement("ul", { className: carouselItemListClass, "data-stid": "carousel-list" }, getCarouselItems()));
};
UitkCarouselItemList.displayName = 'UitkCarouselItemList';

export { Carousel, UitkCarousel, UitkCarouselItem, UitkCarouselItemList, UitkCarouselLocalAnalyticsIdType, UitkCarouselNav };
